import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, Info, AlertCircle, CreditCard, ChevronRight, Euro, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function WordCounter({ text }) {
  const [pricing, setPricing] = useState(null);
  
  useEffect(() => {
    const calculatePrice = async () => {
      try {
        const response = await axios.post(`${API}/pricing/calculate`, { text });
        setPricing(response.data);
      } catch (error) {
        console.error('Pricing error:', error);
      }
    };
    
    // Debounce the API call
    const timer = setTimeout(calculatePrice, 300);
    return () => clearTimeout(timer);
  }, [text]);
  
  if (!pricing) return null;
  
  const progressPercent = Math.min(100, ((pricing.word_count % 500) / 500) * 100);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-muted/50 rounded-lg p-4 border border-border"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-primary" />
          <span className="font-semibold text-primary">Calculator Preț</span>
        </div>
        <div className="text-2xl font-bold text-secondary">
          {pricing.price_formatted}
        </div>
      </div>
      
      {/* Word count progress */}
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-muted-foreground">
            <strong>{pricing.word_count}</strong> cuvinte introduse
          </span>
          <span className="text-muted-foreground">
            Tranșa {pricing.tiers_used} ({pricing.tiers_used * 500} cuvinte max)
          </span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            className="h-full bg-secondary rounded-full"
            transition={{ duration: 0.3 }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Mai puteți adăuga <strong>{pricing.words_remaining_in_tier}</strong> cuvinte în această tranșă
        </p>
      </div>
      
      {/* Pricing info */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground bg-card p-2 rounded">
        <Info className="w-4 h-4 flex-shrink-0" />
        <span>{pricing.vat_note}</span>
      </div>
    </motion.div>
  );
}

export function PaymentButton({ wordCount, userId, caseId, disabled, onPaymentSuccess }) {
  const [isLoading, setIsLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  
  useEffect(() => {
    if (wordCount > 0) {
      axios.post(`${API}/pricing/calculate`, { text: 'x '.repeat(wordCount) })
        .then(res => setPricing(res.data))
        .catch(() => {});
    }
  }, [wordCount]);
  
  const handlePayment = async () => {
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        word_count: wordCount,
        origin_url: window.location.origin,
        user_id: userId || null,
        case_id: caseId || null,
      });
      
      // Save pending case info so we can auto-analyze after payment
      if (caseId) {
        localStorage.setItem('pending_payment_case_id', caseId);
      }
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Payment error:', error);
      alert('Eroare la inițializarea plății. Vă rugăm încercați din nou.');
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!pricing || wordCount <= 0) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-6 border-2 border-secondary/30"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-serif text-xl font-bold text-primary flex items-center gap-2">
            <Euro className="w-5 h-5 text-secondary" />
            Cost Analiză
          </h3>
          <p className="text-sm text-muted-foreground">
            {pricing.word_count} cuvinte • {pricing.tiers_used} tranș{pricing.tiers_used > 1 ? 'e' : 'ă'}
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-secondary">{pricing.price_formatted}</div>
          <p className="text-xs text-muted-foreground">TVA inclus</p>
        </div>
      </div>
      
      {/* Pricing breakdown */}
      <div className="bg-white/50 rounded p-3 mb-4 text-sm">
        <div className="flex justify-between py-1 border-b border-border">
          <span>Preț per tranșă (500 cuvinte)</span>
          <span className="font-semibold">10,00 €</span>
        </div>
        <div className="flex justify-between py-1 border-b border-border">
          <span>Număr tranșe</span>
          <span className="font-semibold">× {pricing.tiers_used}</span>
        </div>
        <div className="flex justify-between py-1 font-bold text-primary">
          <span>Total (TVA inclus)</span>
          <span>{pricing.price_formatted}</span>
        </div>
      </div>
      
      {!userId && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>Vă recomandăm să vă autentificați pentru a salva istoricul</span>
        </div>
      )}
      
      <Button
        onClick={handlePayment}
        disabled={disabled || isLoading}
        className="w-full h-12 bg-secondary text-primary hover:bg-secondary/90 font-semibold"
      >
        {isLoading ? (
          <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Se procesează...</>
        ) : (
          <><CreditCard className="w-5 h-5 mr-2" />Plătește {pricing.price_formatted}<ChevronRight className="w-4 h-4 ml-2" /></>
        )}
      </Button>
      
      <p className="text-xs text-center text-muted-foreground mt-3">
        Plata securizată prin Stripe
      </p>
    </motion.div>
  );
}

export function PricingTable() {
  return (
    <div className="bg-card rounded-lg border-2 border-primary/10 overflow-hidden">
      <div className="bg-primary text-white p-4">
        <h3 className="font-serif text-xl font-bold">Grilă de Tarifare</h3>
        <p className="text-white/70 text-sm">Preț bazat pe volumul de informație</p>
      </div>
      
      <div className="p-4">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 text-muted-foreground font-medium">Volum</th>
              <th className="text-right py-2 text-muted-foreground font-medium">Cost</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border">
              <td className="py-3">0 – 500 cuvinte</td>
              <td className="py-3 text-right font-semibold text-secondary">10 €</td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-3">501 – 1.000 cuvinte</td>
              <td className="py-3 text-right font-semibold text-secondary">20 €</td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-3">1.001 – 1.500 cuvinte</td>
              <td className="py-3 text-right font-semibold text-secondary">30 €</td>
            </tr>
            <tr className="bg-muted/30">
              <td className="py-3 font-medium">... și tot așa</td>
              <td className="py-3 text-right font-semibold text-primary">+10 € / 500 cuvinte</td>
            </tr>
          </tbody>
        </table>
        
        <div className="mt-4 p-3 bg-secondary/10 rounded text-sm">
          <p className="font-semibold text-primary">✓ Fără plafon maxim</p>
          <p className="text-muted-foreground">Prețul include TVA conform legislației în vigoare</p>
        </div>
      </div>
    </div>
  );
}
